import React from "react";
import { NeoButton, NeoInput } from "../../../../../components/Common";
import {
    RUNNING_DURATION_TYPES,
    RUNNING_INTENSITY_ZONES,
    RUNNING_SEGMENT_TYPES,
    RUNNING_TARGET_TYPES,
    RUNNING_TERRAIN_TYPES,
} from "../../domain/running/constants";
import RunningHelpInfo from "./RunningHelpInfo";

const getZone = (value) => RUNNING_INTENSITY_ZONES.find(zone => zone.value === value);

const getGroupTarget = (set, group, index) => {
    const groupKey = group.id || group.value || `group_${index}`;
    const existing = set.paceGroupTargetsById?.[groupKey];
    if (existing) return existing;
    
    const arrayExisting = (set.paceGroupTargets || []).find(item => item.groupId === group.id || item.groupId === group.value);
    if (arrayExisting) return arrayExisting;
    
    return existing || set.paceGroupTargets?.[index] || {};
};

const RunningSegmentRow = ({ baseName, setIndex, set = {}, handleChange, onRemove, onDuplicate, onSaveToLibrary, runningPaceGroups = [] }) => {
    const zone = getZone(set.effortZone);
    const activePaceGroups = runningPaceGroups.filter(group => group.enabled !== false);

    return (
        <div className="running-segment-row">
            <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                <div className="d-flex align-items-center gap-2 min-w-0">
                    {zone && (
                        <span
                            className="running-zone-badge"
                            style={{ "--running-zone-color": zone.color }}
                        >
                            {zone.label}
                        </span>
                    )}
                    <NeoInput
                        type="text"
                        name={`${baseName}.${setIndex}.label`}
                        value={set.label || ""}
                        onChange={handleChange}
                        size="sm"
                        placeholder="Nome do segmento"
                        className="running-segment-title"
                    />
                </div>
                <div className="d-flex align-items-center gap-1 flex-shrink-0">
                    {onSaveToLibrary && (
                        <NeoButton type="button" variant="ghost" size="sm" square icon="mdi-content-save-outline" title="Salvar sessão" onClick={() => onSaveToLibrary(set)} />
                    )}
                    {onDuplicate && (
                        <NeoButton type="button" variant="ghost" size="sm" square icon="mdi-content-copy" title="Duplicar segmento" onClick={onDuplicate} />
                    )}
                    <NeoButton type="button" variant="soft-danger" size="sm" square icon="mdi-close" onClick={onRemove} title="Remover segmento" />
                </div>
            </div>

            <div className="row gx-2">
                <div className="col-md-2 col-6">
                    <label className="font-size-10 mb-1 text-muted fw-bold d-block">Tipo <RunningHelpInfo text="Intenção do segmento: aquecimento, corrida, tiro, recuperação, educativo, subida ou desaquecimento." /></label>
                    <NeoInput type="select" name={`${baseName}.${setIndex}.segmentType`} value={set.segmentType || "run"} onChange={handleChange} size="sm">
                        {RUNNING_SEGMENT_TYPES.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}
                    </NeoInput>
                </div>
                <div className="col-md-1 col-6">
                    <label className="font-size-10 mb-1 text-muted fw-bold d-block">Repetir <RunningHelpInfo text="Quantidade de vezes que este segmento se repete dentro do bloco." /></label>
                    <NeoInput type="number" name={`${baseName}.${setIndex}.reps`} value={set.reps || 1} onChange={handleChange} size="sm" min="1" />
                </div>
                <div className="col-md-2 col-6">
                    <label className="font-size-10 mb-1 text-muted fw-bold d-block">Base <RunningHelpInfo text="Como o segmento será medido: distância, tempo, livre, botão de volta, FC ou potência." /></label>
                    <NeoInput type="select" name={`${baseName}.${setIndex}.durationType`} value={set.durationType || "distance"} onChange={handleChange} size="sm">
                        {RUNNING_DURATION_TYPES.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}
                    </NeoInput>
                </div>
                <div className="col-md-2 col-6">
                    <label className="font-size-10 mb-1 text-muted fw-bold d-block">
                        {set.durationType === "time" ? "Tempo (min)" : "Distância"} <RunningHelpInfo text="Preencha a duração principal do segmento. Em tiros pode ser 400m; em fartlek pode ser 1min." />
                    </label>
                    <div className="d-flex gap-1">
                        {set.durationType === "time" ? (
                            <NeoInput type="number" name={`${baseName}.${setIndex}.duration`} value={set.duration || ""} onChange={handleChange} size="sm" min="0" step="0.5" />
                        ) : (
                            <>
                                <NeoInput type="number" name={`${baseName}.${setIndex}.distance`} value={set.distance || ""} onChange={handleChange} size="sm" min="0" step="0.1" />
                                <NeoInput type="select" name={`${baseName}.${setIndex}.distanceUnit`} value={set.distanceUnit || "km"} onChange={handleChange} size="sm" className="running-unit-select">
                                    <option value="km">km</option>
                                    <option value="m">m</option>
                                </NeoInput>
                            </>
                        )}
                    </div>
                </div>
                <div className="col-md-2 col-6">
                    <label className="font-size-10 mb-1 text-muted fw-bold d-block">Alvo <RunningHelpInfo text="Métrica de controle do atleta: pace, FC, potência, cadência, esforço ou sem alvo." /></label>
                    <NeoInput type="select" name={`${baseName}.${setIndex}.targetType`} value={set.targetType || "pace"} onChange={handleChange} size="sm">
                        {RUNNING_TARGET_TYPES.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}
                    </NeoInput>
                </div>
                <div className="col-md-3 col-6">
                    <label className="font-size-10 mb-1 text-muted fw-bold d-block">Pace / Zona / Referência <RunningHelpInfo text="Informe a faixa alvo. Para clube, use também os paces por grupo abaixo." /></label>
                    {set.targetType === "pace" ? (
                        <div className="d-flex gap-1">
                            <NeoInput type="text" name={`${baseName}.${setIndex}.targetPaceMin`} value={set.targetPaceMin || ""} onChange={handleChange} size="sm" placeholder="4:30" />
                            <NeoInput type="text" name={`${baseName}.${setIndex}.targetPaceMax`} value={set.targetPaceMax || ""} onChange={handleChange} size="sm" placeholder="4:50" />
                        </div>
                    ) : (
                        <NeoInput
                            type="text"
                            name={
                                set.targetType === "heart_rate" ? `${baseName}.${setIndex}.heartRateZone` :
                                set.targetType === "power" ? `${baseName}.${setIndex}.powerZone` :
                                set.targetType === "cadence" ? `${baseName}.${setIndex}.cadenceTarget` :
                                `${baseName}.${setIndex}.notes`
                            }
                            value={
                                set.targetType === "heart_rate" ? (set.heartRateZone || "") :
                                set.targetType === "power" ? (set.powerZone || "") :
                                set.targetType === "cadence" ? (set.cadenceTarget || "") :
                                (set.notes || "")
                            }
                            onChange={handleChange}
                            size="sm"
                            placeholder="Ex: Z3, 150-165 bpm, 170 spm"
                        />
                    )}
                </div>
            </div>

            <div className="row gx-2 mt-2">
                <div className="col-md-2 col-6">
                    <label className="font-size-10 mb-1 text-muted fw-bold d-block">Zona <RunningHelpInfo text="Zona de intensidade usada para resumo do treino e controle de carga." /></label>
                    <NeoInput type="select" name={`${baseName}.${setIndex}.effortZone`} value={set.effortZone || "z2"} onChange={handleChange} size="sm">
                        {RUNNING_INTENSITY_ZONES.map(item => <option key={item.value} value={item.value}>{item.label} - {item.desc}</option>)}
                    </NeoInput>
                </div>
                <div className="col-md-2 col-6">
                    <label className="font-size-10 mb-1 text-muted fw-bold d-block">Terreno <RunningHelpInfo text="Onde o segmento será executado: plano, ondulado, subida, misto ou técnico." /></label>
                    <NeoInput type="select" name={`${baseName}.${setIndex}.terrain`} value={set.terrain || "flat"} onChange={handleChange} size="sm">
                        {RUNNING_TERRAIN_TYPES.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}
                    </NeoInput>
                </div>
                <div className="col-md-3 col-12">
                    <label className="font-size-10 mb-1 text-muted fw-bold d-block">Recuperação após <RunningHelpInfo text="Pausa ou trote após o segmento, como 90s trote ou 200m leve." /></label>
                    <NeoInput type="text" name={`${baseName}.${setIndex}.restAfter`} value={set.restAfter || ""} onChange={handleChange} size="sm" placeholder="Ex: 90s trote, 200m leve" />
                </div>
                <div className="col-md-5 col-12">
                    <label className="font-size-10 mb-1 text-muted fw-bold d-block">Observações <RunningHelpInfo text="Instruções técnicas, logística específica ou ajustes para o professor." /></label>
                    <NeoInput type="text" name={`${baseName}.${setIndex}.notes`} value={set.notes || ""} onChange={handleChange} size="sm" placeholder="Técnica, logística ou variação por grupo" />
                </div>
            </div>

            {set.targetType === "pace" && activePaceGroups.length > 0 && (
                <div className="running-segment-pace-targets mt-2">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <span className="font-size-10 fw-bold text-uppercase text-muted">Pace por grupo <RunningHelpInfo text="Defina alvos diferentes para G1, G2, G3. Se ficar vazio, o sistema usa os paces padrão do grupo." /></span>
                        <span className="font-size-10 text-muted">Usado no modo clube</span>
                    </div>
                    <div className="running-segment-pace-grid">
                        {activePaceGroups.map((group, groupIndex) => {
                            const target = getGroupTarget(set, group, groupIndex);
                            const groupKey = group.id || group.value || `group_${groupIndex}`;
                            const targetBase = `${baseName}.${setIndex}.paceGroupTargetsById.${groupKey}`;
                            return (
                                <div key={group.id || group.value || groupIndex} className="running-segment-pace-card">
                                    <strong>{group.label}</strong>
                                    <span>{group.desc}</span>
                                    <div className="d-flex gap-1 mt-1">
                                        <NeoInput
                                            type="text"
                                            name={`${targetBase}.targetPaceMin`}
                                            value={target.targetPaceMin || ""}
                                            onChange={handleChange}
                                            size="sm"
                                            placeholder={group.intervalPace?.split("-")?.[0] || "4:30"}
                                        />
                                        <NeoInput
                                            type="text"
                                            name={`${targetBase}.targetPaceMax`}
                                            value={target.targetPaceMax || ""}
                                            onChange={handleChange}
                                            size="sm"
                                            placeholder={group.intervalPace?.split("-")?.[1] || "4:50"}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default RunningSegmentRow;
